import React, {useState, useCallback, useEffect, useMemo, useRef} from 'react';
import {
    Box, Button, Skeleton,
    Paper, Stack, Table, TableBody,
    TableCell, TableContainer,
    TableHead, TableRow, Typography, Slide,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InputField, SnackbarField} from '@/components/common';
import TablePagination from '@mui/material/TablePagination';
import {CompanyAnnualService} from '@/services/accountService';
import type {CompanyAnnualPage} from '@/services/accountService/companyAnnualService';
import GlDetail from '@/pages/components/account/gl-invoice/GlDetail';
import {GlCreateStepper} from '@/pages/components/account/gl-invoice/create-stepper';

export default function GlList() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<CompanyAnnualPage[]>([]);
    const [snackbar, setSnackbar] = useState<{
        open: boolean; message?: string; severity?: 'success' | 'error'
    }>({open: false});

    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [showDetail, setShowDetail] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const needReloadRef = React.useRef(false);
    const showDetailRef = useRef(false);

    const [filterCompanyName, setFilterCompanyName] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
    const [createOpen, setCreateOpen] = useState(false);

    /**
     * 拉取 GL 列表。
     * @param opts.silent true=无感刷新（保留当前行，不打龙骨）；false/缺省=整表 Skeleton（菜单进入、筛选）
     */
    const load = useCallback(async (opts?: {silent?: boolean}) => {
        const silent = !!opts?.silent;
        // WHY: Detail 返回/创建成功时列表已在屏上，避免 Skeleton 闪屏
        if (!silent) {
            setLoading(true);
        }
        try {
            const resp = await CompanyAnnualService.companyAnnualPage({
                pageNumber: 1,
                pageSize: 10000,
                pageType: 0,
                companyName: filterCompanyName || undefined,
                yearOfAssessment: filterYear || undefined,
                flowType: 2,
                fsStatus: 1,
            });
            const list = (resp?.list || []) as CompanyAnnualPage[];
            setRows(list);
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Load failed');
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    }, [filterCompanyName, filterYear]);

    // 提取返回逻辑为共享回调（Back 按钮和浏览器后退共用）
    const handleDetailBack = useCallback(() => {
        if (!showDetailRef.current) return;
        showDetailRef.current = false;
        setShowDetail(false);
        setTimeout(() => setSelectedRow(null), 400);
        if (needReloadRef.current) {
            needReloadRef.current = false;
            void load({silent: true});
        }
    }, [load]);

    // 监听浏览器后退按钮（popstate）
    useEffect(() => {
        const onPopState = () => {
            if (showDetailRef.current) {
                handleDetailBack();
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [handleDetailBack]);

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            load();
        }, 400);
        return () => clearTimeout(handler);
    }, [filterCompanyName, filterYear]);

    const filteredGroups = useMemo(() => {
        const grouped: Record<string, CompanyAnnualPage[]> = {};
        rows.forEach(r => {
            const key = r.companyName || `unknown-${r.companyId}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(r);
        });
        Object.values(grouped).forEach(list =>
            list.sort((a, b) => (b.yearOfAssessment || '').localeCompare(a.yearOfAssessment || ''))
        );
        return Object.entries(grouped)
            .map(([name, annuals]) => [name, annuals] as [string, CompanyAnnualPage[]])
            .filter(([, annuals]) => annuals.length > 0);
    }, [rows]);

    const filteredTotal = filteredGroups.length;
    const pagedGroups = useMemo(() => {
        const start = (pageNum - 1) * pageSize;
        return filteredGroups.slice(start, start + pageSize);
    }, [filteredGroups, pageNum, pageSize]);

    return (
        <Box sx={{p: '20px', height: '100%', boxSizing: 'border-box'}}>
            <Box sx={{position: 'relative', height: 'calc(100vh - 120px)', minHeight: '70vh'}}>
                {/* List View */}
                <Slide direction="left" in={!showDetail} mountOnEnter unmountOnExit>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pr: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Stack direction="row"  justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                            <Typography variant="h6" fontWeight={700}>
                                General Ledger
                            </Typography>

                            <Stack direction="row" spacing={1} alignItems="center">
                                <InputField size="small" label="Company Name" value={filterCompanyName} onChange={e => {
                                    setPageNum(1);
                                    setFilterCompanyName(e.target.value);
                                }}/>
                                <InputField size="small" label="Assessment Year" value={filterYear}
                                            placeholder="e.g. 2024/25"
                                            onChange={e => {
                                                setPageNum(1);
                                                setFilterYear(e.target.value);
                                            }}/>
                                <Button size ='small'
                                    variant="contained"
                                    color="secondary"
                                    sx={{
                                        borderRadius: theme.shape.borderRadius,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        boxShadow: theme.shadows[1],
                                        '&:hover': {boxShadow: theme.shadows[3]}
                                    }}
                                    onClick={() => setCreateOpen(true)}>
                                    Add GL
                                </Button>
                            </Stack>
                        </Stack>
                        {error && (
                            <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                        )}
                        <TableContainer
                            component={Paper}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'auto',
                                boxShadow: theme.shadows[2],
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Table
                                size="small"
                                stickyHeader
                                sx={{
                                    '& .MuiTableCell-stickyHeader': {
                                        bgcolor: 'background.paper',
                                        zIndex: 3,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 500}}>Company Name</TableCell>
                                        <TableCell sx={{fontWeight: 500}}>Company Chinese Name</TableCell>
                                        <TableCell sx={{fontWeight: 500}}>Records</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && [...Array(pageSize).keys()].map(i => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                            <TableCell><Skeleton variant="text"/></TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && filteredTotal === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">No data</TableCell>
                                        </TableRow>
                                    )}
                                    {!loading && pagedGroups.map(([companyName, annuals]) => {
                                        const isExpanded = expandedCompany === companyName;
                                        const first = annuals[0];
                                        return (
                                            <React.Fragment key={companyName}>
                                                <TableRow hover sx={{cursor: 'pointer'}}
                                                          onClick={() => setExpandedCompany(isExpanded ? null : companyName)}>
                                                    <TableCell>{companyName}</TableCell>
                                                    <TableCell>{first?.companyChineseName || '-'}</TableCell>
                                                    <TableCell sx={{color: 'text.secondary', fontSize: 12}}>
                                                        {annuals.length} record(s)
                                                    </TableCell>
                                                </TableRow>
                                                {isExpanded && (
                                                    <TableRow>
                                                        <TableCell colSpan={3} sx={{bgcolor: 'background.default', p: 0}}>
                                                            <Box sx={{borderTop: `1px solid ${theme.palette.divider}`, pl: '80px'}}>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell sx={{fontWeight: 600}}>Year Of Assessment</TableCell>
                                                                            <TableCell sx={{fontWeight: 600}}>Period</TableCell>
                                                                            <TableCell sx={{fontWeight: 600}}>Workflow</TableCell>
                                                                            <TableCell sx={{fontWeight: 600}}>Action</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {annuals.map((r, idx) => (
                                                                            <TableRow key={String(r.annualId ?? idx)}
                                                                                      hover>
                                                                                <TableCell>{r.yearOfAssessment}</TableCell>
                                                                                <TableCell>
                                                                                    {r.periodFrom && r.periodTo
                                                                                        ? `${r.periodFrom} ~ ${r.periodTo}`
                                                                                        : r.periodTo || '-'}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Typography variant="caption" sx={{
                                                                                        px: 1,
                                                                                        py: 0.25,
                                                                                        borderRadius: 1,
                                                                                        fontWeight: 600,
                                                                                        bgcolor: r.flowName === 'Audit' ? 'primary.light' : 'success.light',
                                                                                        color: r.flowName === 'Audit' ? 'primary.dark' : 'success.dark',
                                                                                    }}>{r.flowName || '-'}</Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    <Button size="small"
                                                                                            variant="contained"
                                                                                            color="secondary"
                                                                                            sx={{
                                                                                                borderRadius: theme.shape.borderRadius,
                                                                                                textTransform: 'none',
                                                                                                fontWeight: 600,
                                                                                                boxShadow: theme.shadows[1],
                                                                                                '&:hover': {boxShadow: theme.shadows[3]},
                                                                                            }}
                                                                                            onClick={() => {
                                                                                                setSelectedRow(r);
                                                                                                setShowDetail(true);
                                                                                                showDetailRef.current = true;
                                                                                                window.history.pushState({ glDetail: true }, '');
                                                                                            }}
                                                                                    >
                                                                                        Detail
                                                                                    </Button>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div" count={filteredTotal}
                            page={Math.max(0, Math.min(Math.ceil(filteredTotal / pageSize) - 1, pageNum - 1))}
                            onPageChange={(_, p) => setPageNum(p + 1)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={e => {
                                setPageSize(parseInt(e.target.value, 10) || 25);
                                setPageNum(1);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            sx={{mt: 1, flexShrink: 0}}
                        />
                    </Box>
                </Slide>

                {/* Detail View */}
                <Slide direction="right" in={showDetail} mountOnEnter unmountOnExit>
                    <Box sx={{position: 'absolute', inset: 0, pl: 2}}>
                        {selectedRow ? (
                            <GlDetail
                                companyId={selectedRow.companyId}
                                annualId={selectedRow.annualId}
                                companyName={selectedRow.companyName}
                                yearOfAssessment={selectedRow.yearOfAssessment}
                                periodFrom={selectedRow.periodFrom}
                                periodTo={selectedRow.periodTo}
                                onBack={() => {
                                    window.history.back();
                                }}
                                onSubmitSuccess={(message) => {
                                    showDetailRef.current = false;
                                    setShowDetail(false);
                                    // 清理 pushState 添加的历史记录
                                    window.history.back();
                                    // 延迟清除 selectedRow，避免 Slide 退出动画期间内容闪烁
                                    setTimeout(() => setSelectedRow(null), 400);
                                    setSnackbar({ open: true, message: message || 'Submitted successfully', severity: 'success' });
                                    void load({silent: true});
                                }}
                            />
                        ) : (
                            <Box sx={{p: 2, color: 'text.secondary'}}>Select a record.</Box>
                        )}
                    </Box>
                </Slide>

                <SnackbarField
                    open={snackbar.open} message={snackbar.message || ''}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(s => ({...s, open: false}))}
                />
            </Box>

            <GlCreateStepper
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onCreated={(message) => {
                    setCreateOpen(false);
                    setSnackbar({open: true, message: message || 'Invoices saved successfully', severity: 'success'});
                    void load({silent: true});
                }}
            />
        </Box>
    );
}
